<template>
  <PopoverRoot v-model="open" :no-overlay="true">
    <header
      :class="[
        open ? 'fixed inset-0 z-40 overflow-y-auto' : '',
        'bg-white shadow-sm lg:static lg:overflow-y-visible z-10',
      ]"
    >
      <div class="px-4 md:px-10 py-[5.5px]">
        <div
          class="relative flex justify-between space-x-5 lg:space-x-8 xl:space-x-12 items-center"
        >
          <div class="flex md:inset-y-0 md:left-0 lg:static xl:col-span-2">
            <div class="flex flex-shrink-0 items-center">
              <NuxtLink :to="localePath('/')">
                <NuxtImg
                  v-if="logo"
                  class="h-[75px] w-[75px] sm:h-[80px] sm:w-[80px]"
                  :src="logo?.image?.filename"
                  :alt="logo?.image?.alt"
                />
              </NuxtLink>
            </div>
          </div>
          <div class="min-w-0 flex-1 md:pl-8 lg:px-0 xl:col-span-5">
            <div
              class="flex items-center px-3 py-2 md:mx-auto md:max-w-3xl lg:mx-0 lg:max-w-none xl:px-0"
            >
              <div class="w-full">
                <DjSearchBar
                  class="hidden md:block"
                  button-type="primary"
                  @on-search="hideMenu = true"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2 lg:hidden">
            <DjIconWithText
              class="lg:hidden"
              :is-disabled="!isAuthenticated"
              :use-u-icon="true"
              :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
              icon="heroicons:shopping-cart"
              :notification="isAuthenticated && nbProducts !== null ? nbProducts : undefined"
              @click.stop="showCart"
            />
            <PopoverTrigger as-child>
              <UButton
                variant="ghost"
                class="relative -mx-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              >
                <span class="absolute -inset-0.5" />
                <UIcon
                  v-if="!open"
                  name="i-heroicons-outline-bars-3"
                  class="block h-6 w-6"
                  aria-hidden="true"
                />
                <UIcon
                  v-else
                  name="i-heroicons-outline-x-mark"
                  class="block h-6 w-6"
                  aria-hidden="true"
                />
              </UButton>
            </PopoverTrigger>
          </div>
          <ClientOnly>
            <div class="hidden lg:flex lg:items-center lg:justify-end xl:col-span-5">
              <DjIconWithText
                v-if="useQuote"
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                :notification="isAuthenticated && nbQuotes !== null ? nbQuotes : undefined"
                icon="heroicons:document-text"
                :text="t('DjHeaderMain.quote')"
                link="/account/quotes"
                class="flex-none"
              />

              <DjIconWithText
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                icon="heroicons:shopping-cart"
                :notification="isAuthenticated && nbProducts !== null ? nbProducts : undefined"
                :text="t('DjHeaderMain.cart')"
                @click="showCart"
              />

              <DjIconWithText
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                icon="heroicons:heart"
                :notification="nbBuyingList"
                :text="t('DjHeaderMain.favourites')"
                link="/buyinglist"
              />

              <NuxtLink to="/auth/login?oci=true">
                <DjIconWithText
                  v-if="!isAuthenticated"
                  :use-u-icon="true"
                  icon-class="header-top-icon"
                  icon="heroicons:user"
                  :text="t('DjHeaderMain.connection')"
                  link="/auth/login?oci=true"
                />
              </NuxtLink>
              <UDropdownMenu
                :items="userNavigation"
                class="border-none"
                :content="{
                  align: 'start',
                  side: 'bottom',
                  sideOffset: 8,
                }"
                :ui="{ content: 'w-48', background: 'bg-white' }"
              >
                <UButton
                  v-if="isAuthenticated"
                  color="neutral"
                  variant="ghost"
                  class="hover:bg-transparent"
                  @click.stop
                >
                  <div v-if="userName" class="flex items-center space-x-2 max-w-[220px]">
                    <NuxtImg
                      v-if="logo"
                      class="size-12 rounded-full"
                      :src="userAvatarUrl?.filename"
                      :alt="userAvatarUrl?.alt"
                    />
                    <div class="truncate">
                      <div class="font-medium text-black text-sm text-left truncate">Bienvenue</div>
                      <div class="font-medium text-black text-sm text-left truncate">
                        {{ userName }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="flex items-center space-x-2">
                    <USkeleton class="h-6 w-6 rounded-full" />
                    <div>
                      <USkeleton class="h-4 w-24 mb-1" />
                      <USkeleton class="h-4 w-32" />
                    </div>
                  </div>
                </UButton>
              </UDropdownMenu>
            </div>
          </ClientOnly>
        </div>
        <DjSearchBar class="block md:hidden py-3 md:py-0" />
      </div>
    </header>

    <PopoverPortal>
      <PopoverContent v-if="!hideMenu" as="nav" class="lg:hidden" aria-label="Global">
        <div
          v-if="isAuthenticated"
          class="bg-white border-t border-gray-200 pb-3 pt-4 px-6 cursor-pointer shadow-xl w-screen h-auto"
        >
          <div class="mx-auto flex max-w-3xl items-center">
            <div>
              <div class="text-base font-medium capitalize text-gray-800">
                {{ userName }}
              </div>
              <div class="text-sm font-medium text-gray-500 truncate max-w-[120px]">
                {{ userEmail }}
              </div>
            </div>
          </div>
          <div class="mx-auto mt-3 max-w-3xl space-y-1">
            <a
              v-for="item in userNavigation"
              :key="item.label"
              :href="item.href"
              class="block rounded-md py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              @click="item.onClick()"
            >
              {{ item.label }}
            </a>
          </div>
          <div class="border-t border-gray-200 mx-auto mt-3 max-w-3xl space-y-1 px-4 sm:px-4">
            <div class="-my-6 divide-y divide-gray-500/10">
              <div class="space-y-2 py-6">
                <UCollapsible v-model:open="openCategories" class="-mx-3">
                  <div
                    class="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  >
                    {{ $t('DjHeaderMain.products') }}
                    <Icon
                      name="heroicons:chevron-down-20-solid"
                      :class="[openCategories ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
                      aria-hidden="true"
                    />
                  </div>

                  <template #content>
                    <div class="mt-2 space-y-2 overflow-auto max-h-[300px]">
                      <DjCategoryCollapsible
                        v-for="item in NavCategories"
                        :key="item.id"
                        :category="item"
                      />
                    </div>
                  </template>
                </UCollapsible>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
  import DjCartLayer from '../../templates/DjCartLayer/DjCartLayer.vue';
  import type { DropdownMenuItem } from '@nuxt/ui';
  import { PopoverRoot, PopoverTrigger, PopoverContent, PopoverPortal } from 'reka-ui';

  defineOptions({
    name: 'DjHeaderMain',
  });
  defineProps({
    userAvatarUrl: {
      type: Object,
      required: false,
    },
  });
  const config = useRuntimeConfig();
  const { t, locale } = useI18n();
  const { logout } = useDjustAuth();
  const userStore = useUserStore();
  const useQuote = config?.public.useQuote;
  const user = computed(() => userStore.user);
  const isAuthenticated = computed(() => userStore.isAuthenticated);
  const router = useRouter();
  const route = useRoute();
  const localePath = useLocalePath();
  const hideMenu = ref(false);
  const { getUserEmail, getUserFullName } = useUser();

  const { getNavigationCategories } = useNavCategory();
  const { categories: NavCategories } = await getNavigationCategories(locale.value);

  const userName = computed(() => {
    if (user.value) return getUserFullName(user.value);
    return '';
  });

  const userEmail = computed(() => {
    if (user.value) getUserEmail(user.value);
    return '';
  });

  const open = ref(false);
  const openCategories = ref(false);

  const userNavigation = ref<DropdownMenuItem[]>([
    { label: t('DjProfilePage.breadcrumb.profile'), href: '/account/user-information' },
    { label: t('DjCustomerAccountPage.account'), href: '/account/customer-account' },
    { label: t('DjAddressesPage.title'), href: '/account/addresses' },
    { label: t('DjBuyingListPage.title'), href: '/buyinglist' },
    {
      label: t('DjHeaderMain.logout'),
      onClick() {
        logOutFunction();
      },
    },
  ]);

  // Handle state of slideover
  const overlay = useOverlay();
  const cartLayer = overlay.create(DjCartLayer);

  function showCart() {
    if (!route.path.startsWith('/checkout')) {
      cartLayer.open();
    }
  }
  function hideCart() {
    cartLayer.close();
  }

  const redirectedPath = ['/checkout', '/auth', '/product'];
  const routePath = computed(() => route.path);
  watch(routePath, (newValue: any) => {
    if (redirectedPath.some((path: string) => newValue.startsWith(path))) {
      hideCart();
    }
  });

  // Load storyblok data
  const storyblokStore = useStoryblokStore();
  storyblokStore.fetchStory('logo');
  storyblokStore.fetchStory('header');
  const logo = computed(() => storyblokStore.getStoryByName('logo'));
  const userAvatar = computed(() => {
    storyblokStore.getStoryByName('header');
  });

  const { getNumberOfProducts } = useCart();
  const cartStore = useCartStore();
  const quoteStore = useQuoteStore();
  const buyingListStore = useBuyingListStore();
  const nbProducts = computed(() =>
    isAuthenticated.value && cartStore?.cart ? getNumberOfProducts(cartStore?.cart) : null
  );
  const nbQuotes = computed(() =>
    isAuthenticated.value && quoteStore?.quotes ? quoteStore?.quotes.length : null
  );
  const nbBuyingList = computed(() =>
    isAuthenticated.value && buyingListStore?.buyinglist.length
      ? buyingListStore.buyinglist.length
      : 0
  );

  async function logOutFunction(): Promise<void> {
    await logout().then(() => {
      open.value = false;
      router.push('/auth/login?oci=true');
    });
  }
</script>
